/**
 * Utilities for identifying which scheduled_game rows have been placed on court
 * so their status can be updated to 'on_court'.
 *
 * Scheduled games are lifecycle-managed via a status column (pending → on_court →
 * completed). Consumption keys off `gameCreation` with origin === scheduled and a
 * non-null scheduledGameId (never matchmaking/manual rows carrying stray IDs).
 */

import { GAME_CREATION_ORIGIN } from '../constants/gameCreation';
import { rosterPlayerIdsFromScheduledGame } from '@badminton/matchmaking-core';

function isTruthyId(id) {
  return id != null && String(id).trim() !== '';
}

/** True when a court/upcoming row roster equals the given player id set. */
export function courtRosterMatchesPlayerIds(row, playerIds) {
  const courtPlayers = [...(row?.team1 || []), ...(row?.team2 || [])].filter(Boolean).map(String);
  const rosterSet = new Set((playerIds || []).filter(Boolean).map(String));
  if (courtPlayers.length === 0 || rosterSet.size === 0) return false;
  return courtPlayers.length === rosterSet.size && courtPlayers.every((id) => rosterSet.has(id));
}

/** Whether a court row should drop scheduled metadata when a scheduled game is cancelled. */
export function shouldClearScheduledGameFromCourtRow(row, scheduledGameId, rosterPlayerIds) {
  if (!row) return false;
  const sid = row?.gameCreation?.scheduledGameId;
  if (isTruthyId(sid) && String(sid).trim() === String(scheduledGameId)) return true;
  if (!rosterPlayerIds?.length || !courtRosterMatchesPlayerIds(row, rosterPlayerIds)) return false;
  // Roster matches the cancelled game — clear even if origin/id drifted (e.g. after realtime merge).
  return true;
}

export const clearedScheduledGameCreationMeta = () => ({
  origin: null,
  algorithm: null,
  isAltered: false,
  scheduledGameId: null,
});

export function clearScheduledGameFromCourtRow(row, scheduledGameId, rosterPlayerIds) {
  if (!shouldClearScheduledGameFromCourtRow(row, scheduledGameId, rosterPlayerIds)) return row;
  return { ...row, gameCreation: clearedScheduledGameCreationMeta() };
}

/**
 * Collect scheduled_game IDs referenced by explicit scheduled placement metadata.
 *
 * Requires `gameCreation.origin === 'scheduled'` so matchmaking courts cannot trigger
 * consumption even if a stale scheduledGameId slips through.
 *
 * @param {Array<{ gameCreation?: { origin?: string|null, scheduledGameId?: string|null } }>} rows - court or upcoming shapes
 * @returns {string[]} Deduplicated array of scheduled game IDs found in metadata.
 */
/**
 * Whether a court/upcoming slot is tied to an active (pending/placed/on_court) scheduled game.
 * Used to lock roster-editing controls — cancelling the scheduled game is the only
 * way to change the lineup while this is true (see cancel_scheduled_game RPC).
 *
 * Locks on `game_creation_origin = scheduled` even when `scheduled_game_id` was not
 * mirrored onto the active court row after promotion (common on playing courts).
 *
 * @param {{ origin?: string|null, scheduledGameId?: string|null }|null|undefined} gameCreation
 * @returns {boolean}
 */
export function isScheduledGameLinkedSlot(gameCreation) {
  if (!gameCreation) return false;
  if (gameCreation.origin === GAME_CREATION_ORIGIN.SCHEDULED) return true;
  if (!isTruthyId(gameCreation.scheduledGameId)) return false;
  // Drift tolerance: scheduled_game_id without origin after realtime merge.
  return (
    gameCreation.origin !== GAME_CREATION_ORIGIN.MATCHMAKING &&
    gameCreation.origin !== GAME_CREATION_ORIGIN.MANUAL
  );
}

/**
 * Whether a court/upcoming assignment row should block roster edits because it
 * carries scheduled-game metadata or matches a placed/on_court scheduled game roster.
 *
 * @param {{ team1?: string[], team2?: string[], gameCreation?: object }|null|undefined} row
 * @param {Array<{ id?: string, status?: string, roster_player_ids?: string[], team1_player1_id?: string, team1_player2_id?: string, team2_player1_id?: string, team2_player2_id?: string }>} [scheduledGames]
 * @returns {boolean}
 */
export function isAssignmentRowLockedByScheduledGame(row, scheduledGames) {
  if (!row) return false;
  if (!Array.isArray(scheduledGames)) scheduledGames = [];

  if (resolveScheduledGameForAssignmentRow(row, scheduledGames)) {
    return true;
  }

  if (!isScheduledGameLinkedSlot(row.gameCreation)) {
    return false;
  }

  // Playing-court rows often keep origin=scheduled without scheduledGameId while the
  // scheduled_games row is on_court (excluded from fetchScheduledGames). Stay locked
  // until cancel clears metadata. If scheduledGameId is set but the game is gone from
  // the active list, treat metadata as stale after cancel.
  const id = row.gameCreation?.scheduledGameId;
  if (isTruthyId(id)) {
    return scheduledGames.some((g) => String(g.id) === String(id));
  }

  return true;
}

const ACTIVE_SCHEDULED_GAME_STATUSES = new Set(['pending', 'placed', 'on_court']);

/**
 * Resolve the scheduled_games row linked to a court/upcoming assignment row.
 * Used to open the cancel modal when the host clicks remove/clear on a locked slot.
 *
 * @param {{ team1?: string[], team2?: string[], gameCreation?: object }|null|undefined} row
 * @param {Array<{ id?: string, status?: string }>} [scheduledGames]
 * @returns {object|null}
 */
export function resolveScheduledGameForAssignmentRow(row, scheduledGames = []) {
  if (!row) return null;
  if (!Array.isArray(scheduledGames)) scheduledGames = [];

  const id = row.gameCreation?.scheduledGameId;
  if (isTruthyId(id)) {
    const byId = scheduledGames.find((g) => String(g.id) === String(id));
    if (byId) return byId;
  }

  const roster = [...(row.team1 || []), ...(row.team2 || [])].filter(Boolean);
  if (roster.length === 0) return null;

  return (
    scheduledGames.find((game) => {
      if (!ACTIVE_SCHEDULED_GAME_STATUSES.has(game?.status)) return false;
      const gameRoster = rosterPlayerIdsFromScheduledGame(game);
      return courtRosterMatchesPlayerIds(row, gameRoster);
    }) ?? null
  );
}

export function collectScheduledGameIdsFromAssignmentRows(rows) {
  const out = [];
  const seen = new Set();
  if (!Array.isArray(rows)) return out;
  for (const row of rows) {
    const gc = row?.gameCreation;
    if (!gc || gc.origin !== GAME_CREATION_ORIGIN.SCHEDULED) continue;
    const id = gc.scheduledGameId;
    if (!isTruthyId(id)) continue;
    const s = String(id);
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}
